<script setup lang="ts">
import { onMounted, ref, useTemplateRef } from "vue";
import { holidays } from "../data/holidays.ts";
import { meetingRooms } from "../data/rooms.ts";

import {
  Calendar,
  createCalendar,
  destroyCalendar,
  TimeGrid,
  ResourceTimeGrid,
  List,
  ResourceTimeline,
  DayGrid,
  Interaction,
} from "@event-calendar/core";
import '@event-calendar/core/index.css';

import { useContextStore, useToastStore } from "../data/store.ts";
import type { Booking, BookingType } from "../data/types";
import GreeterBox from "../components/GreeterBox.vue";
import { d2s as day2str, GET } from "../data/api.ts";
import MeetingRoomEditModal from "../components/MeetingRoomEditModal.vue";

type CalendarEvent = Calendar.Event & { extendedProps?: { booking: Booking } }

const contextStore = useContextStore();
const toaster = useToastStore();

const fetching = ref<boolean>(true);
const calendarRef = useTemplateRef("cal");
const editModal = ref();
let calendar: Calendar;
let revertCallback: (() => void) | null = null;

const roomColours = [
  'rgb(32, 162, 52)',
  'rgb(206, 57, 57)',
  'oklch(70.7% 0.165 254.624)',
  'rgb(168, 81, 255)',
]

const rooms = meetingRooms.map <Calendar.ResourceInput>((room, i) => ({
  id: room.id,
  title: room.name,
  eventBackgroundColor: roomColours[i],
}));

const holidayEvents: CalendarEvent[] = Object.values(holidays).map<CalendarEvent>((holiday, i) => ({
  title: holiday.name,
  start: holiday.date,
  end: holiday.date,
  editable: false,
  allDay: true,
  classNames: ['hover-unhide'],
  id: i,
  styles: ['white-space: nowrap'],
  resourceIds: rooms.map((room) => room.id),
  startEditable: false,
  durationEditable: false,
  display: "auto",
  backgroundColor: "#ac79ff;",
  textColor: "black",
} as CalendarEvent));

function onDelete() {
  try {
    if (editModal.value) editModal.value.clear();
    calendar.refetchEvents();
  } catch (e) {
    console.error(e);
    toaster.toast('Failed to delete!', "FAIL");
  }
}

function onEdit() {
  try {
    if (editModal.value) editModal.value.clear();
    calendar.refetchEvents();
  } catch (e) {
    console.error(e);
    toaster.toast('Failed to save!', "FAIL");
  }
}

function onSave() {
  try {
    if (editModal.value) editModal.value.clear();
    calendar.refetchEvents();
  } catch (e) {
    console.error(e);
    toaster.toast('Failed to save!', "FAIL");
  }
}

function fetchBookings(info: Calendar.FetchInfo) {
  fetching.value = true;
  return new Promise<CalendarEvent[]>((resolve, reject) => {
    GET<Booking[]>("/bookings/", {
      start: day2str(info.start) as string,
      end: day2str(info.end) as string,
      kind: "MEETING" as BookingType,
    })
      .then((bookings_) => {
        fetching.value = false;
        resolve([...holidayEvents, ...bookings_.map((booking) => ({
          id: booking.id,
          resourceIds: [booking.desk],
          start: new Date(booking.begin as Date),  // actually string under the hood, needs conversion
          end: new Date(booking.end as Date), // actually string under the hood, needs conversion
          title: { html: ((booking.series) ? '<span class="bi-collection" /> ' : "") + `${booking.name}` },
          allDay: false,
          classNames: (booking.person === contextStore.user) ? [] : ["ec-preview"],
          editable: booking.person === contextStore.user,
          startEditable: booking.person === contextStore.user,
          durationEditable: booking.person === contextStore.user,
          extendedProps: {
            booking: {
              ...booking,
              begin: new Date(booking.begin as Date),
              end: new Date(booking.end as Date),
              day: new Date(booking.day as Date),
            }
          },
        } as CalendarEvent))]);
      })
      .catch(() => {
        toaster.toast("Failed to load bookings", "WARN");
        reject();
      });
  });
}

const eventClick = (info: Calendar.EventClickInfo) => {
  if (editModal.value) {
    editModal.value.fillExisting(info.event.extendedProps.booking);
  }
};

const eventDrop = (info: Calendar.EventDropInfo) => {
  revertCallback = info.revert;

  const booking = info.event.extendedProps?.booking as Booking | undefined;
  if (editModal.value && booking) {
    editModal.value.fillExisting({
        ...booking,
        begin: info.event.start,
        end: info.event.end,
        day: info.event.start,
        desk: info.event.resourceIds[0],
      }
    );
  }
};
const eventResize = (info: Calendar.EventResizeInfo) => {
  revertCallback = info.revert;
  const booking = info.event.extendedProps?.booking as Booking | undefined;
  if (editModal.value && booking) {
    editModal.value.fillExisting({
        ...booking,
        begin: info.event.start,
        end: info.event.end,
      }
    );
  }
};
const select = (info: Calendar.SelectInfo) => {
  if (editModal.value) {
    editModal.value.prefillNew({
        begin: info.start,
        end: info.end,
        desk: info.resource?.id ?? rooms[0].id,
      }
    );
  }
};

function initCalendar() {
  const calendarElem = calendarRef.value as HTMLDivElement;

  calendar = createCalendar(
    // HTML element the calendar will be mounted to
    calendarElem,
    // Array of plugins
    [DayGrid, Interaction, List, ResourceTimeGrid, ResourceTimeline, TimeGrid],
    // Options object
    {
      events: holidayEvents,
      resources: rooms,
      view: 'resourceTimeGridWeek',
      headerToolbar: {
        start: 'prev,next today',
        center: 'title',
        end: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek resourceTimeGridWeek,resourceTimelineWeek'
      },
      scrollTime: '09:00',
      views: {
        timeGridWeek: { pointer: true },
        resourceTimeGridWeek: {
          // columnWidth: '6.9em',
          pointer: true
        },
        resourceTimelineWeek: {
          // slotDuration: '00:15',
          // slotLabelInterval: '02:00',
          scrollTime: '08:00',
        }
      },
      slotHeight: 60,
      slotMinTime: '07:00:00',
      slotMaxTime: '19:30:00',
      dayMaxEvents: true,
      nowIndicator: true,
      selectable: true,
      firstDay: 1,
      hiddenDays: [0, 6],
      eventSources: [{
        events: fetchBookings,
      }],
      eventClick,
      eventDrop,
      eventResize,
      select,
      selectConstraint: (info: Calendar.SelectInfo) => (info.start.toISOString().slice(0, 10) === info.end.toISOString().slice(0, 10)),
      dragConstraint: (info: Calendar.EventDragInfo) => (info.event.start.toISOString().slice(0, 10) === info.event.end.toISOString().slice(0, 10)),
      resizeConstraint: (info: Calendar.EventResizeInfo) => (info.event.start.toISOString().slice(0, 10) === info.event.end.toISOString().slice(0, 10)),
    }
  );
}

onMounted(async () => {
  if (calendar) await destroyCalendar(calendar);
  initCalendar();
});

function onCancel() {
  if (revertCallback) revertCallback();
}

// // If you later need to destroy the calendar then use
// destroyCalendar(ec);

</script>

<template>
  <div v-if="contextStore.isMobile" class="badge text-bg-warning mt-5">
    Room booking is not optimised for mobile/touch, yet. Please use a computer
    instead.
  </div>
  <GreeterBox>
    You can reserve a meeting room below. Please choose the smallest room
    possible.
    <!-- FIXME: add configuration back in (show weekend and after hours) -->
    <!--    <template #icons>-->
    <!--      <i-->
    <!--        class="bi-gear icon-btn"-->
    <!--        role="button"-->
    <!--        @click="showSettings = !showSettings"-->
    <!--      />-->
    <!--    </template>-->

  </GreeterBox>

  <p class="lead">&nbsp;<template v-if="fetching">Please wait while data is being loaded...</template>
  </p>
  <div ref="cal" />

  <MeetingRoomEditModal
    ref="editModal"
    @deleted="onDelete"
    @edited="onEdit"
    @saved="onSave"
    @canceled="onCancel"
  />
</template>
<style>
.ec-event-title {
  font-size: 1.15rem !important;
}

.ec-all-day .ec-event-title {
  white-space: break-spaces !important;
  text-wrap: wrap !important;
  overflow-wrap: break-word !important;
}

.ec-preview {
  cursor: context-menu;
}

/*
.ec-event-body:hover > h4 {
  width: calc(var(--ec-col-width) * 1.5);
  background-color: var(--ec-event-bg-color);
  color: var(--ec-event-text-color);
}
*/
</style>
